import Backbone from '@polleverywhere/backbone'
import env from '@polleverywhere/core/lib/env'
import { cleanString } from '@polleverywhere/core/lib/util'
import Tpl from './tpl.hamlc'
import { QRCode } from '@polleverywhere/view-qrcode'
import qrcodeIcon from '@polleverywhere/view-qrcode/lib/icons/qrcode.svg'

/**
 * Returns the i18n arguments to format messages for SMS responses.
 */
function i18nSmsArguments () {
  const safeAttribute = attr => {
    return cleanString(this.model.get(attr))
  }

  return {
    keyword: `<span class="emphasis keyword">${safeAttribute('keyword')}</span>`,
    instruction_word: `<span class="emphasis code instruction_word">${this.instructionWord()}</span>`,
    keyword_format: `<span class="emphasis keyword_format">${safeAttribute('session_keyword_format_description')}</span>`,
    session_keyword: `<span class="emphasis session_keyword">${cleanString(this.model.presenter.get('session_keyword'))}</span>`,
    shortcode: `<span class="emphasis shortcode">${safeAttribute('shortcode')}</span>`
  }
}

/**
 * Returns the i18n arguments to format messages for web responses.
 */
function i18nWebArguments () {
  const loginText = this.i18n({
    id: 'poll_inst_login',
    default: 'Log in',
    description: 'Poll instruction: **Log in** to activate'
  })

  const presenterURL = env
    .get('mobile_url')
    .path(this.model.presenter.get('login'))
  let presenterHumanURL = ''
  try {
    presenterHumanURL = presenterURL.host() + '/' + presenterURL.path()
  } catch (e) {
    if (window.Rollbar) {
      window.Rollbar.error(`Something went wrong: ${JSON.stringify(this.model.presenter)}`, e)
    }
  }

  return {
    url: `<a class="url emphasis" target="_blank" rel="noopener" href="${presenterURL.toString()}">${presenterHumanURL}</a>`,
    login: `<span class="deactivated-login"><span class="instructions-login emphasis">${loginText}</span></span>`
  }
}

export default class Open extends Backbone.View {
  get className () { return 'open' }

  get events () {
    return {
      'click .show_regions': 'showRegionSelection',
      'change .regions': 'changeRegion',
      'click .web--qrcode__button': 'showQRCode'
    }
  }

  /**
   * @param {Backbone.Model} model
   * @param {Backbone.Model} settings
   */
  initialize (options = {}) {
    if (!options.i18n) {
      console.error('i18n is required.')
    }

    this.i18n = options.i18n

    this.render = this.render.bind(this)
    this.renderSMS = this.renderSMS.bind(this)
    this.toggleWeb = this.toggleWeb.bind(this)
    this.toggleSMS = this.toggleSMS.bind(this)
    this.changeRegion = this.changeRegion.bind(this)
    this.showRegionSelection = this.showRegionSelection.bind(this)
    this.hideRegionSelection = this.hideRegionSelection.bind(this)
    this.toggleRegions = this.toggleRegions.bind(this)
    this.renderServiceAreaId = this.renderServiceAreaId.bind(this)
    this.renderWeb = this.renderWeb.bind(this)
    this.isActive = this.isActive.bind(this)
    this.isPresenter = this.isPresenter.bind(this)
    this.instructionWord = this.instructionWord.bind(this)

    this.settings = options.settings
    this.user = options.user

    this.listenTo(this.model, 'change:web_enabled', this.toggleWeb)
    this.listenTo(this.model, 'change:sms_enabled', this.toggleSMS)
    this.listenTo(this.model, 'change:simple_keywords', this.renderSMS)
    this.listenTo(this.model, 'change:keyword', this.renderSMS)
    this.listenTo(this.model, 'change:shortcode', this.renderSMS)
    if (this.model.presenter) {
      this.listenTo(this.model.presenter, 'change:session_keyword', this.renderSMS)
    }
    if (this.settings) {
      this.listenTo(this.settings, 'change:regions', this.toggleRegions)
    }
  }

  render () {
    this.$el.html(Tpl())
    this.renderServiceAreaId()
    this.toggleRegions()
    this.renderWeb()
    this.toggleWeb()
    this.toggleQRCodeButton()
    this.renderSMS()
    this.toggleSMS()
    return this
  }

  toggleWeb () {
    const webEnabled = this.model.get('web_enabled')
    this.$('.modality.web').toggleClass('hidden', !webEnabled)
  }

  toggleSMS () {
    const smsEnabled = this.model.get('sms_enabled')
    this.$('.modality.sms').toggleClass('hidden', !smsEnabled)
  }

  changeRegion () {
    if (!this.settings) { return }
    const $target = this.$('.regions :selected')
    const val = $target.val()

    // tell reloader to use the new service area
    this.settings.set({ service_area_id: val })

    // cache locally
    this.service_area_id = val
    this.hideRegionSelection()
  }

  showRegionSelection () {
    this.$('.show_regions').addClass('hidden')
    this.$('.regions').removeClass('hidden')
  }

  hideRegionSelection () {
    this.$('.regions').addClass('hidden')
    this.$('.show_regions').removeClass('hidden')
  }

  toggleRegions () {
    if (!this.settings) { return }
    this.$('.regions_container').toggleClass('hidden', !this.settings.get('regions'))
  }

  renderServiceAreaId () {
    if (this.service_area_id || (this.settings && this.settings.get('service_area_id'))) {
      const id = this.service_area_id || this.settings.get('service_area_id')
      this.$(`.regions option[value='${id}']`).attr('selected', 'selected')
    }
  }

  renderSMS () {
    let html
    const args = i18nSmsArguments.call(this)

    if (this.model.get('simple_keywords')) {
      // This is a presenter session. Must be active to vote.
      if (this.isActive() || (this.isPresenter() && this.user.isLoggedIn())) {
        if (this.model.type() === 'free_text_poll') {
          html = this.i18n('Text {session_keyword} to {shortcode} once to join, then text your message', args)
        } else {
          html = this.i18n('Text {session_keyword} to {shortcode} once to join, then {keyword_format}', args)
        }
      } else {
        html = this.i18n('Text {session_keyword} to {shortcode} once to join', args)
      }
    } else {
      // Keyword is username or other identifier
      if (this.model.get('keyword')) {
        html = this.i18n('Text {keyword} and your message to {shortcode}', args)
      } else {
        // global keywords - globally unique keywords, poll does not need to be active.
        html = this.i18n('Text a {instruction_word} to {shortcode}', args)
      }
    }

    this.$('.modality.sms .sms--text').html(html)

    return this.renderServiceAreaId()
  }

  renderWeb () {
    const $icon = this.$('.web--icon')
    const $text = this.$('.web--text')
    const args = i18nWebArguments.call(this)

    // If rendered inside a presenter app, always display active instructions.
    if (this.isActive() || this.isPresenter()) {
      $icon
        .attr('m-icon-medium', '')
        .attr('m-icon-wide', '')
      return $text.html(this.i18n('Respond at {url}', args))
    } else {
      $icon
        .attr('m-icon-medium', '')
        .attr('m-icon-wide', null)
      return $text.html(
        this.i18n('When poll is active, respond at {url}', args)
      )
    }
  }

  isActive () {
    return this.model.isActivated()
  }

  isPresenter () {
    if (!this.settings) { return false }
    return this.settings.get('push') === 'voting_instructions'
  }

  instructionWord () {
    if (typeof this.model.hasAlphaNumericKeyword === 'function' && this.model.hasAlphaNumericKeyword()) {
      return this.i18n({
        default: 'KEYWORD',
        description:
          'Used when alphanumeric code is used to respond to the poll, e.g. 7ELEVEN'
      })
    } else {
      return this.i18n({
        default: 'CODE',
        description:
          'Used when numeric code is used to respond to the poll, e.g. 56122'
      })
    }
  }

  toggleQRCodeButton () {
    const hasPollevQrcodeLabsFlag = this.user &&
      this.user.get('features') &&
      this.user.get('features').pollev_qrcodes

    const $qrcode = this.$('.web--qrcode')
    const $qrcodeImage = this.$('.web--qrcode__button')

    if (hasPollevQrcodeLabsFlag) {
      $qrcode.toggleClass('hidden', false)
      $qrcodeImage.html(qrcodeIcon)

      if (this.settings.forces('qrcode')) {
        this.showQRCode()
      }
    }
  }

  showQRCode () {
    const qrcode = new QRCode({
      user: this.user
    })

    this.trigger('qrcode')
    this.$('.web--qrcode').append(qrcode.render().$el)
  }
}
